import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Check, ChevronDown, X, Cog } from "lucide-react";

/**
 * Multi-select for Warmachine MK4 model types, grouped by category, plus a
 * cross-cutting "Advantages" group (Massive, Construct, Living, etc).
 * Catalog fetched from /api/model-types once on mount.
 *
 * Props:
 *   value     : string[]   array of selected ids
 *   onChange  : (ids[]) => void
 *   disabled  : boolean
 *   testId    : string    base for data-testid (default "model-types")
 */
export function ModelTypeSelect({ value = [], onChange, disabled = false, testId = "model-types" }) {
  const [catalog, setCatalog] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    api.getModelTypes()
      .then((data) => { if (alive) setCatalog(data); })
      .catch(() => { if (alive) setCatalog({ categories: [], advantages: [] }); });
    return () => { alive = false; };
  }, []);

  const allItems = React.useMemo(() => {
    if (!catalog) return [];
    return [
      ...catalog.categories.flatMap((c) => c.items),
      ...(catalog.advantages || []),
    ];
  }, [catalog]);

  const labelById = React.useMemo(() => {
    const m = {};
    for (const it of allItems) m[it.id] = it.label;
    return m;
  }, [allItems]);

  const toggle = (id) => {
    const set = new Set(value);
    if (set.has(id)) set.delete(id);
    else set.add(id);
    onChange(Array.from(set));
  };

  const remove = (id) => {
    onChange(value.filter((v) => v !== id));
  };

  const clear = () => onChange([]);

  return (
    <div className="space-y-1" data-testid={testId}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className="w-full justify-between font-normal"
            disabled={disabled || !catalog}
            data-testid={`${testId}-trigger`}
          >
            <span className="flex items-center gap-1.5 truncate">
              <Cog className="w-3.5 h-3.5 text-primary" />
              {value.length > 0
                ? `${value.length} model type${value.length === 1 ? "" : "s"} selected`
                : "Model type(s) — what the rule applies to…"}
            </span>
            <ChevronDown className="w-3.5 h-3.5 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[520px] max-h-[60vh] overflow-y-auto p-2"
          align="start"
          data-testid={`${testId}-popover`}
        >
          {!catalog && <p className="text-xs text-muted-foreground p-2">Loading…</p>}
          {catalog && (
            <div className="space-y-3">
              {value.length > 0 && (
                <div className="flex items-center justify-between gap-2 px-2 pt-1">
                  <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
                    Selected ({value.length})
                  </span>
                  <button
                    type="button"
                    className="text-xs text-muted-foreground hover:text-rose-300 inline-flex items-center gap-0.5"
                    onClick={clear}
                    data-testid={`${testId}-clear`}
                  >
                    <X className="w-3 h-3" /> clear
                  </button>
                </div>
              )}

              {catalog.categories.map((cat) => (
                <div key={cat.id}>
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground px-2 mb-1">
                    {cat.label}
                  </p>
                  <div className="grid grid-cols-2 gap-0.5">
                    {cat.items.map((it) => {
                      const on = value.includes(it.id);
                      return (
                        <button
                          key={it.id}
                          type="button"
                          onClick={() => toggle(it.id)}
                          className={`text-left px-2 py-1.5 rounded text-xs border transition ${
                            on
                              ? "border-primary/60 bg-primary/15 text-primary"
                              : "border-transparent hover:bg-muted/50 text-foreground"
                          }`}
                          data-testid={`${testId}-opt-${it.id}`}
                          aria-pressed={on}
                        >
                          <span className="flex items-center gap-1.5">
                            {on ? <Check className="w-3 h-3" /> : <span className="w-3" />}
                            <span className="font-medium">{it.label}</span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </PopoverContent>
      </Popover>

      {value.length > 0 && (
        <div className="flex flex-wrap gap-1" data-testid={`${testId}-chips`}>
          {value.map((id) => (
            <Badge
              key={id}
              variant="outline"
              className="text-[10px] text-primary border-primary/40 bg-primary/10 gap-1 pr-1"
              data-testid={`${testId}-chip-${id}`}
            >
              {labelById[id] || id}
              <button
                type="button"
                onClick={() => remove(id)}
                className="hover:text-rose-300"
                disabled={disabled}
                aria-label={`Remove ${labelById[id] || id}`}
              >
                <X className="w-3 h-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

export function ModelTypeChips({ ids = [], catalog = null }) {
  // Read-only chips for displaying selected model types in History etc.
  const [labels, setLabels] = useState(null);
  useEffect(() => {
    if (catalog) {
      const m = {};
      for (const c of (catalog.categories || [])) for (const it of c.items) m[it.id] = it.label;
      for (const a of (catalog.advantages || [])) m[a.id] = a.label;
      setLabels(m);
    } else {
      api.getModelTypes().then((d) => {
        const m = {};
        for (const c of (d.categories || [])) for (const it of c.items) m[it.id] = it.label;
        for (const a of (d.advantages || [])) m[a.id] = a.label;
        setLabels(m);
      }).catch(() => setLabels({}));
    }
  }, [catalog]);
  if (!ids?.length) return null;
  return (
    <div className="flex flex-wrap gap-1" data-testid="model-type-chips">
      {ids.map((id) => (
        <Badge key={id} variant="outline" className="text-[10px] text-primary border-primary/40 bg-primary/10">
          {(labels && labels[id]) || id}
        </Badge>
      ))}
    </div>
  );
}
